import {
  CalendarUnit,
  DateColumnName,
  dateColumnNameToCalendarUnit,
  ECalendarSemanticTypes,
  IColumnSource,
  IDatasetLensColumn,
  isColumnAggregation,
  isColumnDataSource
} from '@opengov/reporting-types';
import { DateTime } from 'luxon';

export const dateStringErrorMessage = (dateString: string): string => {
  if (dateString === 'Empty Values' || dateString === '' || !dateString) {
    return 'Empty Values';
  }
  // CellErrorTypes from capital
  if (dateString === '__Invalid Date') {
    return 'Invalid Date';
  }
  if (dateString === '__Unexpected Value') {
    return 'Unexpected Value';
  }

  if (isNaN(parseInt(dateString, 10))) {
    return 'Invalid Date';
  }

  return '';
};

export const toFormattedDate = (dateString: string, format: string): string => {
  return DateTime.fromISO(dateString, {
    zone: 'utc',
    locale: typeof window !== 'undefined' ? window?.navigator?.language : 'en-US'
  }).toFormat(format);
};

export const toLocalizedCalendarDate = (dateString: string, format: Intl.DateTimeFormatOptions): string => {
  return DateTime.fromISO(dateString, {
    zone: 'utc',
    locale: typeof window !== 'undefined' ? window?.navigator?.language : 'en-US'
  }).toLocaleString(format);
};

export const calendarDateFormats = {
  year: { year: 'numeric' },
  monthYear: { year: 'numeric', month: 'long' },
  dayMonthYear: { year: 'numeric', month: '2-digit', day: '2-digit' },
  minuteHourDayMonthYear: { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' }
} as const;

const fiscalMonths = [1, 1, 1, 2, 2, 2, 3, 3, 3, 4, 4, 4];

export const toFiscalQuarterAndYear = (dateString: string, fiscalYearStartMonth: number): string => {
  // DateTime.fromISO('2020-01-01T00:00:00.000Z').month returns 12 in PDX!
  // The dates coming off the queries can have the T..Z which are in UTC and have already been
  // translated to a FY start of Jan
  const dateTime = DateTime.fromISO(dateString, { zone: 'utc' }).toUTC();
  const month = dateTime.month;
  const paddedOffset = `${fiscalYearStartMonth}`.padStart(2, '0');
  const fiscalMonth = month < fiscalYearStartMonth ? 12 - fiscalYearStartMonth + month : month - fiscalYearStartMonth;
  // remember that arrays are zero based. fiscalMonth === 0 for January, etc.
  const quarter = fiscalMonths[fiscalMonth];

  const fiscalYear =
    month >= fiscalYearStartMonth
      ? DateTime.fromISO(`${dateTime.year}-${paddedOffset}-01`).plus({ years: 1 }).minus({ days: 1 }).year
      : dateTime.year;

  return `Q${quarter} FY${fiscalYear}`;
};

export const toFiscalYear = (dateString: string): string => {
  return toFormattedDate(dateString, `'FY'yyyy`);
};

export const toPeriodString = (year: string, period: string): string => {
  return `${year}-P${period.padStart(3, '0')}`;
}

export const parsePeriodString = (periodString: string): { year: number, period: number } => {
  const lowerCasePeriodString = periodString.toLowerCase();
  const [year, period] = lowerCasePeriodString.includes('-p')
    ? lowerCasePeriodString.split('-p')
    : periodString.split(' ');

  return { year: Number(year), period: Number(period) };
};

export const toFiscalPeriod = (periodString: string): string => {
  if (!periodString) {
    return '';
  }
  const { year, period } = parsePeriodString(periodString);
  return `Period ${period} FY${year}`;
};

export const calendarUnitFormatters = {
  [CalendarUnit.Day]: {
    formatter: () => (dateString) =>
      dateStringErrorMessage(dateString) || toLocalizedCalendarDate(dateString, calendarDateFormats.dayMonthYear)
  },
  [CalendarUnit.Week]: {
    formatter: () => (dateString) =>
      dateStringErrorMessage(dateString) || toLocalizedCalendarDate(dateString, calendarDateFormats.dayMonthYear)
  },
  [CalendarUnit.Month]: {
    formatter: () => (dateString) =>
      dateStringErrorMessage(dateString) || toLocalizedCalendarDate(dateString, calendarDateFormats.monthYear)
  },
  [CalendarUnit.Quarter]: {
    formatter: () => (dateString) => dateStringErrorMessage(dateString) || toFormattedDate(dateString, `'Q'q yyyy`)
  },
  [CalendarUnit.FiscalQuarter]: {
    formatter: (params: { entityFiscalYearStartMonth: number }) => (dateString) => {
      const entityFiscalYearStartMonth = params && params.entityFiscalYearStartMonth;
      return dateStringErrorMessage(dateString) || toFiscalQuarterAndYear(dateString, entityFiscalYearStartMonth);
    }
  },
  [CalendarUnit.Year]: {
    formatter: () => (dateString) =>
      dateStringErrorMessage(dateString) || toLocalizedCalendarDate(dateString, calendarDateFormats.year)
  },
  [CalendarUnit.FiscalYear]: {
    formatter: () => (dateString) => dateStringErrorMessage(dateString) || toFiscalYear(`${parseInt(dateString, 10)}`)
  },
  [CalendarUnit.FiscalPeriod]: {
    // A period/year combo can't effectively be conveyed with an ISO date.
    // Instead, the organize-by query for fiscal period/year returns a string
    // like "2018 13" for Period 13, FY 2017-18 or "2017-P001" for Period 1 FY 2017.
    formatter: () => (periodString) => dateStringErrorMessage(periodString) || toFiscalPeriod(periodString)
  }
};

export const getCalendarUnit = (columnSource: IColumnSource, lensColumns?: IDatasetLensColumn[]): CalendarUnit => {
  if (isColumnAggregation(columnSource)) return null;
  let calendarUnit: CalendarUnit = null;
  if (isColumnDataSource(columnSource)) {
    const grouping = columnSource.grouping[1];
    if (grouping.hasOwnProperty('columnName')) {
      const { columnName } = grouping as { columnName: string };
      calendarUnit = dateColumnNameToCalendarUnit[columnName];
      const lensColumn = lensColumns?.find((column) => column.name === columnName);
      if (lensColumn) {
        const semanticTypes = new Set(lensColumn.semanticTypes.flatMap((t) => t));
        if (semanticTypes.has(ECalendarSemanticTypes.FISCAL_YEAR)) {
          calendarUnit = dateColumnNameToCalendarUnit[DateColumnName.FiscalYear];
        }
      }
    }
  }
  return calendarUnit;
};
